import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { FormSection } from '../../components/ui/FormSection';
import { TextField } from '../../components/ui/TextField';
import { Toggle } from '../../components/ui/Toggle';
import { Button } from '../../components/ui/Button';
import { IconButton } from '../../components/ui/IconButton';
import { AutoSaveStatus } from '../../components/ui/AutoSaveStatus';
import { useAutoSave } from '../../lib/useAutoSave';
import { uid, type Workflow } from '../../types';

function blankDraft(): Workflow {
  return {
    id: '',
    title: 'New Workflow',
    nickname: '@workflow',
    trigger: 'Signal received',
    actions: [{ id: uid(), title: 'Notify', kind: 'Alert' }],
    active: true,
  };
}

export function WorkflowScreen() {
  const { channelId, workflowId } = useParams();
  const isNew = !workflowId;
  const navigate = useNavigate();
  const { channel, ensureChannelLoaded, updateWorkflow } = useStore();

  const [draft, setDraft] = useState<Workflow>(blankDraft);
  const [newActionTitle, setNewActionTitle] = useState('');
  const [newActionKind, setNewActionKind] = useState('Alert');

  // See WorkflowListScreen.tsx's identical call - needed so `save`/the
  // auto-save below have a real channel (in `channels` or at least
  // `viewedChannels`) to patch the moment this screen mounts, instead of
  // racing the app's initial channel-list sync.
  useEffect(() => {
    ensureChannelLoaded(channelId ?? '');
  }, [channelId, ensureChannelLoaded]);

  useEffect(() => {
    if (channelId && workflowId) {
      const existing = channel(channelId)?.workflows.find((w) => w.id === workflowId);
      if (existing) setDraft(existing);
    }
  }, [channelId, workflowId]); // eslint-disable-line react-hooks/exhaustive-deps

  const addAction = () => {
    if (!newActionTitle.trim()) return;
    setDraft((d) => ({ ...d, actions: [...d.actions, { id: uid(), title: newActionTitle.trim(), kind: newActionKind }] }));
    setNewActionTitle('');
  };

  // Create path only - an existing workflow auto-saves (below).
  const save = () => {
    if (channelId) updateWorkflow({ ...draft, id: draft.id || uid() }, channelId);
    navigate(`/channels/${channelId}/workflows`);
  };

  // Edit path: debounced auto-save. draft.id is '' until the existing
  // workflow loads, which doubles as the "ready" gate.
  const auto = useAutoSave(
    draft,
    (d) => {
      if (channelId) updateWorkflow(d, channelId);
    },
    { enabled: !isNew && draft.id !== '' },
  );

  return (
    <div className="flex flex-col gap-5" onBlur={isNew ? undefined : auto.flush}>
      <ScreenHeader
        title={isNew ? 'Create workflow' : 'Edit workflow'}
        action={isNew ? <IconButton icon={Check} onClick={save} aria-label="Save" /> : <AutoSaveStatus status={auto.status} onRetry={auto.retry} />}
      />

      <FormSection title="Workflow">
        <TextField label="Title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
        <TextField
          label="Nickname"
          value={draft.nickname}
          onChange={(e) => setDraft({ ...draft, nickname: e.target.value })}
          disabled={!isNew}
          title={!isNew ? "A workflow's nickname can't be changed" : undefined}
        />
        <TextField
          label="Trigger"
          value={draft.trigger}
          onChange={(e) => setDraft({ ...draft, trigger: e.target.value })}
        />
        <Toggle label="Active" checked={draft.active} onChange={(v) => setDraft({ ...draft, active: v })} />
      </FormSection>

      <FormSection title="Actions">
        <div className="flex flex-col divide-y divide-black/5 dark:divide-white/10">
          {draft.actions.map((action) => (
            <div key={action.id} className="flex items-center justify-between py-2">
              <span>{action.title}</span>
              <span className="text-xs text-black/40 dark:text-white/40">{action.kind}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <TextField placeholder="Action" value={newActionTitle} onChange={(e) => setNewActionTitle(e.target.value)} />
          <TextField placeholder="Kind" value={newActionKind} onChange={(e) => setNewActionKind(e.target.value)} />
          <Button variant="bordered" onClick={addAction}>
            Add
          </Button>
        </div>
      </FormSection>
    </div>
  );
}
